const PROFIT_COLORS = {
  invoices: "#64dd01",
  orders: "#007FFF",
  noCosts: "#9c27b0",
};

export function getPedidoProfitMeta(row) {
  const hasInvoices =
    Number(row.InvSalesMoneda || 0) > 0 && Number(row.InvCostMoneda || 0) !== 0;

  const hasPurchase =
    Number(row.PedidoCompraE || 0) !== 0 || Number(row.InvCostMoneda || 0) !== 0;

  let profitAbs = 0;
  let profitPct = 0;
  let type = "none";

  if (hasInvoices) {
    type = "invoices";
    profitAbs =
      Math.abs(Number(row.InvSalesMoneda)) - Math.abs(Number(row.InvCostMoneda));
    profitPct =
      ((Number(row.InvSalesMoneda) - Math.abs(Number(row.InvCostMoneda))) /
        Number(row.InvSalesMoneda)) *
      100;
  } else if (hasPurchase && Number(row.VentaBaseEUR || 0) > 0) {
    type = "orders";
    profitAbs =
      Math.abs(Number(row.VentaBaseEUR)) - Math.abs(Number(row.PedidoCompraE || 0));
    profitPct =
      ((Number(row.VentaBaseEUR) - Math.abs(Number(row.PedidoCompraE || 0))) /
        Number(row.VentaBaseEUR)) *
      100;
  } else if (!hasPurchase) {
    type = "noCosts";
  }

  return {
    profitAbs,
    profitPct,
    type,
    color: PROFIT_COLORS[type] || undefined,
    hasInvoices,
    hasPurchase,
  };
}

export function formatPedidoAmount(val, { decimals = 2, emptyZero = false } = {}) {
  if (val === undefined || val === null) return "";
  if (emptyZero && Number(val) === 0) return "";
  return Number(val).toLocaleString("es-ES", {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  });
}

export function formatProfitPct(profitPct) {
  return `${Number(profitPct).toLocaleString("es-ES", { maximumFractionDigits: 2 })} %`;
}

export const EMPRESA_OPTIONS = [
  { value: "stage", label: "Stage" },
  { value: "erna", label: "ERNA" },
  { value: "fusion", label: "Fusión" },
];

export const PROFIT_LEGEND = [
  { key: "orders", label: "Pedido", color: PROFIT_COLORS.orders },
  { key: "invoices", label: "Factura", color: PROFIT_COLORS.invoices },
  { key: "noCosts", label: "Sin coste", color: PROFIT_COLORS.noCosts },
];

const DOC_KIND_COLORS = {
  invoices: PROFIT_COLORS.invoices,
  orders: PROFIT_COLORS.orders,
};

export function getPedidoCliente(row) {
  return String(row?.CLIPRO || "").trim();
}

export function getPedidoDoc(row) {
  return String(row?.PorchDoc || "").trim();
}

export function getPedidoEstado(row) {
  const ptes = Number(row?.UN_Ptes || 0);
  const serv = Number(row?.UN_Serv || 0);
  if (ptes > 0) return "abierto";
  if (serv > 0) return "servido";
  return "cerrado";
}

/** Factura = hay importe de factura de venta. Si no, sigue siendo pedido. */
export function getPedidoDocKind(row) {
  return Number(row?.InvSalesMoneda || 0) > 0 ? "invoices" : "orders";
}

export function getPedidoDocKindMeta(row) {
  const kind = getPedidoDocKind(row);
  const fromLegend = PROFIT_LEGEND.find((item) => item.key === kind);
  return {
    kind,
    label: fromLegend?.label || "Pedido",
    color: DOC_KIND_COLORS[kind] || PROFIT_COLORS.orders,
  };
}

export function listPedidoClientes(rows = []) {
  return [
    ...new Set(
      (Array.isArray(rows) ? rows : [])
        .map((row) => getPedidoCliente(row))
        .filter(Boolean),
    ),
  ].sort((a, b) => a.localeCompare(b, "es"));
}

export function matchesPedidoVentaSearch(row, search) {
  const q = String(search || "")
    .trim()
    .toLowerCase();
  if (!q) return true;
  return [
    row?.CLIPRO,
    row?.REPALIAS,
    row?.ARTALIAS,
    row?.PorchDoc,
    row?.CAR1,
    row?.CODART,
  ].some((value) => String(value || "").toLowerCase().includes(q));
}

export function filterPedidosVenta(
  rows = [],
  { search = "", estado = "todos", cliente = "", docKind = "todos" } = {},
) {
  const clienteKey = String(cliente || "").trim();
  return (Array.isArray(rows) ? rows : []).filter((row) => {
    if (!matchesPedidoVentaSearch(row, search)) return false;
    if (estado === "abiertos" && getPedidoEstado(row) !== "abierto") return false;
    if (estado === "servidos" && getPedidoEstado(row) !== "servido") return false;
    if (clienteKey && getPedidoCliente(row) !== clienteKey) return false;
    if (docKind !== "todos" && getPedidoDocKind(row) !== docKind) return false;
    return true;
  });
}

export const PEDIDO_VENTA_ORDEN_DEFAULT = "entrega-asc";

export const PEDIDO_VENTA_ORDEN_OPTIONS = [
  { value: "entrega-asc", label: "Entrega: cercanas primero" },
  { value: "entrega-desc", label: "Entrega: lejanas primero" },
  { value: "fecha-desc", label: "Fecha PO: nuevas primero" },
  { value: "fecha-asc", label: "Fecha PO: antiguas primero" },
  { value: "cliente", label: "Cliente A → Z" },
];

function pedidoDateMs(row, field) {
  const raw = row?.[field];
  if (!raw) return null;
  const ms = new Date(raw).getTime();
  return Number.isFinite(ms) ? ms : null;
}

export function sortPedidosVenta(rows = [], orden = PEDIDO_VENTA_ORDEN_DEFAULT) {
  const list = [...(Array.isArray(rows) ? rows : [])];
  const cmpDate = (field, dir) => (a, b) => {
    const av = pedidoDateMs(a, field);
    const bv = pedidoDateMs(b, field);
    if (av == null && bv == null) return 0;
    if (av == null) return 1;
    if (bv == null) return -1;
    return (av - bv) * dir;
  };

  if (orden === "entrega-desc") return list.sort(cmpDate("DeliveryDate", -1));
  if (orden === "fecha-asc") return list.sort(cmpDate("FECHA", 1));
  if (orden === "fecha-desc") return list.sort(cmpDate("FECHA", -1));
  if (orden === "cliente") {
    return list.sort((a, b) =>
      getPedidoCliente(a).localeCompare(getPedidoCliente(b), "es"),
    );
  }
  return list.sort(cmpDate("DeliveryDate", 1));
}

export function createPedidoFocus(type, row) {
  const cliente = getPedidoCliente(row);
  const doc = getPedidoDoc(row);
  if (type === "cliente") {
    if (!cliente) return null;
    return { type: "cliente", cliente };
  }
  if (!cliente && !doc) return null;
  return { type: "doc", cliente, doc };
}

export function isSamePedidoFocus(a, b) {
  if (!a || !b || a.type !== b.type) return false;
  if (a.type === "cliente") return a.cliente === b.cliente;
  return a.cliente === b.cliente && a.doc === b.doc;
}

export function togglePedidoFocus(current, next) {
  if (!next) return null;
  return isSamePedidoFocus(current, next) ? null : next;
}

export function rowMatchesPedidoFocus(row, focus) {
  if (!focus) return true;
  if (focus.type === "cliente") return getPedidoCliente(row) === focus.cliente;
  return getPedidoCliente(row) === focus.cliente && getPedidoDoc(row) === focus.doc;
}

export function describePedidoFocus(focus, count) {
  if (!focus) return "";
  if (focus.type === "cliente") {
    return `${focus.cliente} · ${count} ${count === 1 ? "línea" : "líneas"}`;
  }
  const doc = focus.doc || "sin PO";
  return `${focus.cliente || "Cliente"} · ${doc} · ${count} ${
    count === 1 ? "línea" : "líneas"
  }`;
}

export function getPedidoPendingAmount(row) {
  const ptes = Number(row?.UN_Ptes || 0);
  const unit = Number(row?.UNPedVentaE || 0);
  if (ptes > 0 && Number.isFinite(unit) && unit > 0) return ptes * unit;
  const sale = Math.abs(Number(row?.VentaBaseEUR || 0));
  const invoiced = Math.abs(Number(row?.InvSalesMoneda || 0));
  const gap = sale - invoiced;
  return gap > 0 ? gap : 0;
}

const INITIAL_SKIP =
  /^(S\.?A\.?|S\.?L\.?|B\.?V\.?|LTD|LIMITED|INC|GMBH|EUROPE|INTERNATIONAL|TRADING|THE|AND|DE|DEL|LA|EL|&)$/i;

export function getClienteInitials(name) {
  const parts = String(name || "")
    .trim()
    .split(/[\s,/]+/)
    .map((part) => part.replace(/[()[\]{}]/g, ""))
    .filter((part) => part && !INITIAL_SKIP.test(part));
  const use = parts.length ? parts : String(name || "").trim().split(/\s+/).filter(Boolean);
  if (!use.length) return "?";
  if (use.length === 1) return use[0].slice(0, 2).toUpperCase();
  return `${use[0][0]}${use[1][0]}`.toUpperCase();
}

const AVATAR_PALETTE = [
  "#1976d2",
  "#00897b",
  "#6d4c41",
  "#7b1fa2",
  "#1565c0",
  "#2e7d32",
  "#c62828",
  "#ef6c00",
  "#455a64",
  "#5c6bc0",
];

export function getClienteAvatarColor(name) {
  const text = String(name || "");
  let hash = 0;
  for (let i = 0; i < text.length; i += 1) {
    hash = (hash * 31 + text.charCodeAt(i)) >>> 0;
  }
  return AVATAR_PALETTE[hash % AVATAR_PALETTE.length];
}

export function summarizePedidoDoc(rows = []) {
  const list = Array.isArray(rows) ? rows : [];
  let venta = 0;
  let facturado = 0;
  let pendiente = 0;
  let ptes = 0;
  let serv = 0;
  let un = 0;
  for (const row of list) {
    venta += Number(row?.VentaBaseEUR || 0);
    facturado += Number(row?.InvSalesMoneda || 0);
    pendiente += getPedidoPendingAmount(row);
    ptes += Number(row?.UN_Ptes || 0);
    serv += Number(row?.UN_Serv || 0);
    un += Number(row?.VentasUN || 0);
  }
  const estado = ptes > 0 ? "abierto" : serv > 0 ? "servido" : "cerrado";
  return {
    venta,
    facturado,
    pendiente,
    ptes,
    serv,
    un,
    estado,
    count: list.length,
  };
}

export function summarizePedidos(rows = []) {
  let totalSales = 0;
  let totalProfit = 0;
  let pendingUnits = 0;
  let pendingAmount = 0;

  (Array.isArray(rows) ? rows : []).forEach((row) => {
    totalSales += Number(row.VentaBaseEUR || row.InvSalesMoneda || 0);
    totalProfit += getPedidoProfitMeta(row).profitAbs;
    pendingUnits += Number(row.UN_Ptes || 0);
    pendingAmount += getPedidoPendingAmount(row);
  });

  return {
    totalSales,
    totalProfit,
    pendingUnits,
    pendingAmount,
    count: rows.length,
  };
}

export function getYearOptions(count = 6) {
  const current = new Date().getFullYear();
  return Array.from({ length: count }, (_, i) => current - i);
}
